import { ErrorModal, HelmetDexteel, useHasProfile } from "@dexteel/mesf-core";
import {
	Box,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Group, Panel } from "react-resizable-panels";

import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { CustomSeparator } from "@/pages/catalogs/shared/components/CustomSeparator";
import { useResizablePanels } from "@/pages/catalogs/shared/hooks/useResizablePanels";
import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import { SupplierSelector } from "@/shared/selectors";
import { pageBgColor } from "@/utils/theme";
import { SupplierHeatDetailsPanel } from "./components/SupplierHeatDetailsPanel";
import { SupplierHeatsTable } from "./components/SupplierHeatsTable";
import {
	ChemistryDraft,
	ElementEvaluation,
	SupplierHeatRow,
	SupplierHeatsPayload,
} from "./models/ChemistryDraft";
import {
	acknowledgeSupplierHeat,
	getSupplierHeats,
	updateSupplierHeatGrade,
	upsertChemistryResults,
} from "./repositories/SupplierHeatsRepository";

export const elementText = (element: ElementEvaluation) => {
	if (element.Value === null || element.Value === undefined)
		return element.RawValue ?? "";
	if (element.IsBelowDetection)
		return (
			element.RawValue ?? `<${String(Number(element.Value.toPrecision(6)))}`
		);
	return String(Number(element.Value.toPrecision(6)));
};

export const buildChemistryDraft = (
	reportHeatId: number,
	elements: ElementEvaluation[],
): ChemistryDraft => ({
	ReportHeatId: reportHeatId,
	Values: Object.fromEntries(
		elements.map((element) => [element.ElementId, elementText(element)]),
	),
});

export const changedResults = (
	draft: ChemistryDraft,
	original: ChemistryDraft,
) =>
	Object.entries(draft.Values)
		.filter(
			([elementId, text]) =>
				text.trim() !== (original.Values[Number(elementId)] ?? "").trim(),
		)
		.map(([elementId, text]) => ({
			ElementId: Number(elementId),
			RawValue: text.trim() === "" ? null : text.trim(),
		}));

const useSupplierHeats = ({
	lookbackDays,
	supplierId,
}: {
	lookbackDays: number;
	supplierId: number | null;
}) =>
	useQuery<SupplierHeatsPayload, Error>({
		queryKey: ["supplier-heats", lookbackDays, supplierId],
		queryFn: ({ signal }) => {
			const startDate = lookbackDays > 0 ? new Date() : null;
			startDate?.setDate(startDate.getDate() - lookbackDays);
			return getSupplierHeats({ startDate, supplierId }, signal);
		},
		refetchInterval: 60_000,
	});

export const SupplierHeatsPage = () => {
	const queryClient = useQueryClient();
	const hasProfile = useHasProfile();
	const canEdit = hasProfile("Lab Profile") || hasProfile("admin Profile");

	const [error, setError] = useState("");
	const [draft, setDraft] = useState<ChemistryDraft | null>(null);
	const [originalDraft, setOriginalDraft] = useState<ChemistryDraft | null>(
		null,
	);
	const [pendingHeat, setPendingHeat] = useState<SupplierHeatRow | null>(null);
	const [confirmDiscard, setConfirmDiscard] = useState(false);
	const [lookbackDays, setLookbackDays] = useState(30);
	const [supplierId, setSupplierId] = useState<number | null>(null);
	const [quickFilter, setQuickFilter] = useState("");
	const [selectedReportHeatId, setSelectedReportHeatId] = useState<
		number | null
	>(null);

	const {
		groupRef,
		getSavedPanelLayout,
		handlePanelResize,
		handleSeparatorDoubleClick,
	} = useResizablePanels("supplier-heats-panel-sizes");

	const {
		data,
		isLoading,
		error: queryError,
		isError,
	} = useSupplierHeats({ lookbackDays, supplierId });

	const selectedHeat =
		data?.heats.find((heat) => heat.ReportHeatId === selectedReportHeatId) ??
		null;

	const changes =
		draft && originalDraft ? changedResults(draft, originalDraft) : [];
	const isDirty = changes.length > 0;

	const saveMutation = useMutation({
		mutationFn: upsertChemistryResults,
		meta: { successMessage: "The chemistry was saved successfully" },
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["supplier-heats"],
			});
		},
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	const acknowledgeMutation = useMutation({
		mutationFn: acknowledgeSupplierHeat,
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["supplier-heats"],
			});
		},
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	const gradeMutation = useMutation({
		mutationFn: updateSupplierHeatGrade,
		meta: { successMessage: "The grade was updated successfully" },
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["supplier-heats"],
			});
		},
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	const toggleAcknowledge = (heat: SupplierHeatRow) =>
		acknowledgeMutation.mutate({
			reportHeatId: heat.ReportHeatId,
			markAsAcknowledge: heat.AcknowledgedAt == null,
		});

	const cancelEdit = () => {
		setDraft(null);
		setOriginalDraft(null);
		setConfirmDiscard(false);
	};

	const requestCancelEdit = () => {
		if (isDirty) {
			setConfirmDiscard(true);
			return;
		}

		cancelEdit();
	};

	const startEdit = () => {
		if (!selectedHeat) return;

		const editDraft = buildChemistryDraft(
			selectedHeat.ReportHeatId,
			(data?.elements ?? []).filter(
				(element) => element.ReportHeatId === selectedHeat.ReportHeatId,
			),
		);

		setDraft(editDraft);
		setOriginalDraft(editDraft);
	};

	const saveDraft = () => {
		if (!draft || !isDirty) return;

		saveMutation.mutate({ reportHeatId: draft.ReportHeatId, results: changes });
		cancelEdit();
	};

	const selectHeat = (heat: SupplierHeatRow) => {
		if (heat.ReportHeatId === selectedReportHeatId) return;

		if (isDirty) {
			setPendingHeat(heat);
			setConfirmDiscard(true);
			return;
		}

		cancelEdit();
		setSelectedReportHeatId(heat.ReportHeatId);
	};

	const handleConfirmDiscard = (value?: string) => {
		setConfirmDiscard(false);

		if (value !== "OK") {
			setPendingHeat(null);
			return;
		}

		cancelEdit();
		if (pendingHeat) setSelectedReportHeatId(pendingHeat.ReportHeatId);
		setPendingHeat(null);
	};

	useEffect(() => {
		if (isError) {
			setError(queryError.message);
		}
	}, [isError, queryError]);

	return (
		<>
			<HelmetDexteel title="Supplier Heats" />
			<Stack
				spacing={1}
				sx={{ backgroundColor: pageBgColor, px: 2, pt: 2, pb: 1 }}
			>
				<Typography variant="h5" fontWeight={600}>
					Supplier Heats
				</Typography>

				<Stack
					direction="row"
					spacing={1}
					alignItems="center"
					flexWrap="wrap"
					useFlexGap
				>
					<FormControl size="small" sx={{ minWidth: 160 }}>
						<InputLabel id="supplier-heats-lookback-label">
							Report Date
						</InputLabel>
						<Select
							labelId="supplier-heats-lookback-label"
							label="Report Date"
							value={lookbackDays}
							onChange={(e) => setLookbackDays(Number(e.target.value))}
							sx={{
								backgroundColor: (theme) => theme.palette.background.paper,
							}}
						>
							{[7, 30, 90, 365].map((days) => (
								<MenuItem key={days} value={days}>
									{`Last ${days} days`}
								</MenuItem>
							))}
							<MenuItem value={0}>All</MenuItem>
						</Select>
					</FormControl>
					<Box sx={{ minWidth: 200 }}>
						<SupplierSelector value={supplierId} onChange={setSupplierId} />
					</Box>
					<Box sx={{ minWidth: 260, mt: -1 }}>
						<TextFieldSearchFilter
							value={quickFilter}
							onChange={setQuickFilter}
						/>
					</Box>
				</Stack>

				<Box sx={{ height: "calc(100vh - 210px)" }}>
					<Group
						orientation="vertical"
						defaultLayout={getSavedPanelLayout()}
						onLayoutChange={handlePanelResize}
						groupRef={groupRef}
						style={{ height: "100%" }}
					>
						<Panel id="table" minSize={25}>
							<SupplierHeatsTable
								rows={data?.heats ?? []}
								isLoading={isLoading}
								quickFilter={quickFilter}
								selectedReportHeatId={selectedReportHeatId}
								canAcknowledge={canEdit}
								isAcknowledging={acknowledgeMutation.isPending}
								onSelectHeat={selectHeat}
								onToggleAcknowledge={toggleAcknowledge}
							/>
						</Panel>

						<CustomSeparator
							onDoubleClick={handleSeparatorDoubleClick}
							orientation="vertical"
						/>

						<Panel id="details" minSize={25}>
							<Paper elevation={2} sx={{ height: "100%", width: "100%" }}>
								<SupplierHeatDetailsPanel
									heat={selectedHeat}
									elements={data?.elements ?? []}
									sectionLimits={data?.sectionLimits ?? []}
									version={data?.version ?? null}
									canEdit={canEdit}
									draft={draft}
									isDirty={isDirty}
									isSaving={saveMutation.isPending}
									onStartEdit={startEdit}
									onDraftChange={setDraft}
									onCancel={requestCancelEdit}
									onSave={saveDraft}
									isAcknowledging={acknowledgeMutation.isPending}
									onToggleAcknowledge={toggleAcknowledge}
									isSavingGrade={gradeMutation.isPending}
									onSaveGrade={(heat: SupplierHeatRow, gradeId: number) =>
										gradeMutation.mutate({
											reportHeatId: heat.ReportHeatId,
											gradeId,
										})
									}
								/>
							</Paper>
						</Panel>
					</Group>
				</Box>
			</Stack>
			<ConfirmationDialogRaw
				open={confirmDiscard}
				title="Discard Changes"
				message="The chemistry has unsaved changes. Discard them?"
				buttons={{ no: "Keep Editing", yes: "Discard" }}
				onClose={handleConfirmDiscard}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
